import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import { useNavigate } from "react-router-dom";
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

export default function TestIaPage() {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(t("settings.admin.testIa.defaultPrompt"));
  const [modele, setModele] = useState("gpt-5-nano");
  const [result, setResult] = useState("");
  const [status, setStatus] = useState(null); // success | error | loading
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [withSearch, setWithSearch] = useState(false);
  const [sentJson, setSentJson] = useState(""); // Ajout pour afficher le JSON envoyé
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [functionKey, setFunctionKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(true);

  // Détection de l'environnement local (GitHub Codespace)
  // Vite expose les variables d'environnement avec le préfixe VITE_
  // Pour les variables système, on peut vérifier via window.location
  const isCodespace = window.location.hostname.includes('app.github.dev');
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isLocal = isCodespace || isLocalhost;
  
  const getApiUrl = () => {
    const baseUrl = import.meta.env.VITE_FUNCTION_URL;
    return `${baseUrl}/api/AskOpenAI`;
  };

  const apiUrl = getApiUrl();

  // Récupérer la clé de fonction depuis Supabase
  useEffect(() => {
    const getFunctionKey = async () => {
      try {
        const keyName = isLocal ? 'FUNCTION_KEY_DEV' : 'FUNCTION_KEY';
        const { data, error } = await supabase
          .from('keys')
          .select('value')
          .eq('env', keyName)
          .single();

        if (error) {
          console.error("Erreur lors de la récupération de la clé:", error);
        } else if (data) {
          setFunctionKey(data.value);
        }
      } catch (err) {
        console.error("Exception lors de la récupération de la clé:", err);
      } finally {
        setKeyLoading(false);
      }
    };
    
    getFunctionKey();
  }, [isLocal]);

  // Récupérer l'utilisateur connecté au chargement du composant
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          console.error("Erreur lors de la récupération de l'utilisateur:", error);
        } else {
          setCurrentUser(user);
        }
      } catch (err) {
        console.error("Exception lors de la vérification de l'authentification:", err);
      } finally {
        setAuthChecked(true);
      }
    };
    
    getUser();
  }, []);

  const handleTest = async (e) => {
    e.preventDefault();
    
    // Vérifier que l'utilisateur est connecté
    if (!currentUser) {
      setStatus("error");
      setMessage(t('common.notAuthenticated'));
      return;
    }

    // Vérifier que la clé de fonction est disponible
    if (!functionKey) {
      setStatus("error");
      setMessage(t('settings.admin.testIa.functionKeyMissing'));
      return;
    }

    setLoading(true);
    setStatus("loading");
    setMessage(t("settings.admin.testIa.status.loading"));
    setResult("");
    const payload = { prompt, modele, withSearch };
    setSentJson(JSON.stringify(payload, null, 2));
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-functions-key": functionKey
      };
      
      const res = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      // Lis le body une seule fois
      const rawText = await res.text();
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (jsonErr) {
        setStatus("error");
        setMessage(
          t("settings.admin.testIa.status.error") +
          ` (HTTP ${res.status})\n` +
          t("settings.admin.testIa.status.parseError") +
          "\n" +
          rawText
        );
        setResult("");
        setLoading(false);
        return;
      }
      if (res.ok) {
        setStatus("success");
        setMessage(t("settings.admin.testIa.status.success"));
        setResult(JSON.stringify(data, null, 2));
      } else {
        setStatus("error");
        let msg = data.message;
        if (typeof msg === "string") {
          setMessage(msg + ` (HTTP ${res.status})`);
        } else if (msg) {
          setMessage(JSON.stringify(msg, null, 2) + ` (HTTP ${res.status})`);
        } else {
          setMessage(
            t("settings.admin.testIa.status.error") +
            ` (HTTP ${res.status})`
          );
        }
        setResult(JSON.stringify(data, null, 2));
      }
    } catch (err) {
      setStatus("error");
      setMessage(t("settings.admin.testIa.status.error") + ": " + err.message);
      setResult("");
    }
    setLoading(false);
  };

  // Si l'état de l'authentification n'est pas encore vérifié, afficher un indicateur de chargement
  if (adminLoading || !authChecked || keyLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title={t("settings.admin.testIa.title")} backPath="/settings" />
      <div className="p-4">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
          {!currentUser ? (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
              <p className="text-amber-700">{t('common.pleaseLogin')}</p>
              <button 
                className="mt-2 bg-[#2894D8] text-white px-4 py-2 rounded hover:bg-[#136E9D] transition-colors"
                onClick={() => window.location.href = '/login'}
              >
                {t('common.login')}
              </button>
            </div>
          ) : !functionKey ? (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <p className="text-red-700">{t('settings.admin.testIa.functionKeyMissing')}</p>
            </div>
          ) : (
            <>
              <div className="mb-2 text-xs text-gray-500 text-right">
                {isLocal
                  ? `${t("settings.admin.testIa.env.local")} - ${apiUrl}`
                  : `${t("settings.admin.testIa.env.production")} - ${apiUrl}`}
              </div>
              <form onSubmit={handleTest} className="space-y-4">
                <div>
                  <label className="block font-medium mb-1">{t("settings.admin.testIa.promptLabel")}</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg p-2"
                    rows={4}
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">{t("settings.admin.testIa.modelLabel")}</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg p-2"
                    value={modele}
                    onChange={e => setModele(e.target.value)}
                  />
                </div>
                <div>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox accent-[#2894D8] mr-2"
                      checked={withSearch}
                      onChange={e => setWithSearch(e.target.checked)}
                    />
                    {t("settings.admin.testIa.withSearchLabel")}
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-2 rounded-lg bg-[#2894D8] text-white font-semibold hover:bg-[#136E9D] transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {loading ? t("settings.admin.testIa.loading") : t("settings.admin.testIa.testButton")}
                </button>
              </form>
              {status && (
                <div
                  className={`mt-6 rounded-lg p-3 text-sm ${
                    status === "success"
                      ? "bg-green-100 text-green-800"
                      : status === "error"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-200 text-yellow-800"
                  }`}
                >
                  <strong>{t("settings.admin.testIa.status.message")}</strong>
                  <div>{message}</div>
                </div>
              )}
              {sentJson && (
                <div className="mt-4 bg-gray-50 rounded-lg p-3 text-sm whitespace-pre-wrap border border-gray-200">
                  <strong>{t("settings.admin.testIa.sentJsonLabel")}</strong>
                  <div>{sentJson}</div>
                </div>
              )}
              {result && (
                <div className="mt-4 bg-gray-100 rounded-lg p-3 text-sm whitespace-pre-wrap">
                  <strong>{t("settings.admin.testIa.resultLabel")}</strong>
                  <div>{result}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
